/**
 * Conversion helpers between `Date` and the string format
 * `<input type="datetime-local" step="1">` expects/produces
 * (`YYYY-MM-DDTHH:mm:ss`, always in the *local* timezone -- the input
 * element has no timezone concept of its own). Kept dependency-free and
 * pure so the manual-timestamp-entry requirement (master spec: "Enter
 * precise timestamps manually" as an independent alternative to
 * timeline-handle selection) is unit-testable without mounting any
 * component.
 */

function pad(n: number, width = 2): string {
  return n.toString().padStart(width, '0')
}

/** `null`/`undefined` in -> `''` out, so the input simply renders empty rather than "Invalid Date". */
export function dateToLocalInputValue(date: Date | null | undefined): string {
  if (date == null || Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

/** `''`/unparseable in -> `null` out. Never throws. */
export function localInputValueToDate(value: string): Date | null {
  if (value.trim() === '') return null
  // New Date(string) parses "YYYY-MM-DDTHH:mm:ss" (and the minute-granularity
  // "YYYY-MM-DDTHH:mm" the browser can still emit for `step`-less inputs) as
  // local time per the HTML spec for this value format, consistently across
  // browsers and under jsdom.
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** `YYYY-MM-DDTHH:mm[:ss[.fff]]` -- the DOM normalizes a datetime-local value to any of these. */
const INPUT_VALUE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?$/

/**
 * `Date` -> `datetime-local` value in the app's time display mode: the
 * instant's UTC wall-clock time in `'utc'` mode, local time otherwise.
 * The mode only changes how the absolute instant is *written*, never the
 * instant itself (`model/display-time.ts`).
 */
export function dateToInputValue(date: Date | null | undefined, mode: 'utc' | 'local'): string {
  if (mode === 'local') return dateToLocalInputValue(date)
  if (date == null || Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 19)
}

/**
 * `datetime-local` value -> absolute `Date`, reading the wall-clock time as
 * UTC (`'utc'` mode) or local time. `''`/malformed in -> `null` out.
 */
export function inputValueToDate(value: string, mode: 'utc' | 'local'): Date | null {
  if (mode === 'local') return localInputValueToDate(value)
  const match = INPUT_VALUE_PATTERN.exec(value.trim())
  if (match == null) return null
  const [y, mo, d, h, mi, s] = match.slice(1).map((part) => (part == null ? 0 : Number(part)))
  if ((h ?? 0) > 23 || (mi ?? 0) > 59 || (s ?? 0) > 59) return null
  const date = new Date(Date.UTC(y ?? 0, (mo ?? 1) - 1, d, h, mi, s))
  // Reject rollover (e.g. Feb 30) rather than silently moving the date.
  return date.getUTCDate() === d && date.getUTCMonth() === (mo ?? 1) - 1 ? date : null
}
